import type { ChangeEvent } from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextFieldLabel from '@shared/components/inputs/TextFieldLabel/TextFieldLabel';
import DatePickerField from '@shared/components/inputs/DatePickerField';
import { FormikSelectField } from '@shared/components/inputs/FormikSelectField';
import { FormikTextField } from '@shared/components/inputs/FormikTextField';
import { sanitizeNameInput } from '@utils/nameInput';
import {
  HOW_DID_YOU_HEAR_OPTIONS,
  getDobBounds,
  textFieldSx,
} from '@components/Checkout/steps/CustomerInfoStep/utils/customerInfoStepUtils';
import type { PersonalDetailsSectionProps } from '@components/Checkout/steps/CustomerInfoStep/CustomerInfoStep.types';

const NAME_COLUMN_SX = { width: { xs: '100%', sm: '33%' } };

export const PersonalDetailsSection = ({ formik, titleOptions }: PersonalDetailsSectionProps) => {
  const { values, errors, touched, setFieldValue, setFieldTouched } = formik;
  const { minDateValue, today } = getDobBounds();
  const dobError = touched.dateOfBirth && !!errors.dateOfBirth;
  const dobHelperText = dobError ? (errors.dateOfBirth as string) : '';

  const handleHowDidYouHearChange = (value: string | number) => {
    if (value !== 'Other') {
      setFieldValue('howDidYouHearAboutUsOther', '');
    }
  };

  // SC-11595 — names go downstream to provisioning; constrain input to the
  // English/French alphabets and name punctuation as the user types.
  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFieldValue(event.target.name, sanitizeNameInput(event.target.value));
  };

  return (
    <>
      <Typography variant="heading.subsection" sx={{ lineHeight: '150%' }}>
        Personal Details
      </Typography>

      <FormikSelectField
        formik={formik}
        name="titleId"
        label="Title"
        optional
        placeholder="Select Option"
        options={titleOptions}
      />

      <Stack direction={{ xs: 'column', sm: 'row' }} gap="15px" alignItems="flex-start">
        <FormikTextField
          formik={formik}
          name="firstName"
          label="First Name"
          placeholder="Enter your first name..."
          inputSx={textFieldSx}
          containerSx={NAME_COLUMN_SX}
          onChange={handleNameChange}
        />
        <FormikTextField
          formik={formik}
          name="middleName"
          label="Middle Name"
          optional
          placeholder="Enter your middle initial..."
          inputSx={textFieldSx}
          containerSx={NAME_COLUMN_SX}
          onChange={handleNameChange}
        />
        <FormikTextField
          formik={formik}
          name="lastName"
          label="Last Name"
          placeholder="Enter your last name..."
          inputSx={textFieldSx}
          containerSx={NAME_COLUMN_SX}
          onChange={handleNameChange}
        />
      </Stack>

      <Stack sx={{ width: { xs: '100%' } }} gap="10px">
        <Stack>
          <TextFieldLabel label="Date of Birth" />
          <Typography id="dob-disclaimer" variant="body.sm" color="border.black">
            We use this information to verify your identity when you connect with our team.
          </Typography>
        </Stack>
        <DatePickerField
          name="dateOfBirth"
          ariaLabel="Date of Birth"
          ariaDescribedBy="dob-disclaimer"
          value={values.dateOfBirth}
          onChange={(value) => setFieldValue('dateOfBirth', value)}
          onBlur={() => setFieldTouched('dateOfBirth', true)}
          minDate={minDateValue}
          maxDate={today}
          sx={textFieldSx}
          error={dobError}
          helperText={dobHelperText}
        />
      </Stack>

      <FormikSelectField
        formik={formik}
        name="howDidYouHearAboutUs"
        label="How did you hear about us?"
        optional
        placeholder="Select Option"
        options={HOW_DID_YOU_HEAR_OPTIONS}
        onValueChange={handleHowDidYouHearChange}
      />

      {values.howDidYouHearAboutUs === 'Other' && (
        <FormikTextField
          formik={formik}
          name="howDidYouHearAboutUsOther"
          label="Please Specify"
          placeholder="Tell us how you heard about us..."
          inputSx={textFieldSx}
        />
      )}
    </>
  );
};
